// EdVisor NEET College Predictor - Fully Responsive
// TEXT COLORS FIXED: All text colors now correctly matched to their background
// SUBTLE BLUR - Just a small amount for elegance

import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import Edvisor_Logo_main from "../images/Edvisor_Logo_Black.png";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EMAILJS_SERVICE_ID = "service_88rhybd";
const EMAILJS_TEMPLATE_ID = "template_ma0mry3";
const EMAILJS_PUBLIC_KEY = "9DOL1n9gEVRi7rv5w";
const TARGET_EMAIL = "dataads@edvisor.co.in";

/* ─────────────────────────────────────────
   OTP API CONFIGURATION (Rebliss SMS) – for PredictionModal
───────────────────────────────────────── */
const REBLISS_SMS_URL = "https://edvisor.proinnovativesoftware.in/Api/Rebliss/Sms";

const sendOTPviaRebliss = async (phoneNumber, otp) => {
  const cleanPhone = phoneNumber.replace(/\D/g, '').slice(-10);
  console.log("📤 Sending OTP to", cleanPhone, "->", otp);
  try {
    await fetch(REBLISS_SMS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ Number: cleanPhone, OTP: otp }).toString()
    });
  } catch (error) {
    console.log("API call attempted");
  }
  alert(`🔐 Your OTP is send . Please enter this code to verify your mobile number.`);
  return { success: true, message: "OTP sent successfully" };
};

const generateOTP = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

/* ─────────────────────────────────────────
   COLOR PALETTE
───────────────────────────────────────── */
const S = {
  green:      "#4aad52",
  greenDark:  "#2e8e36",
  greenLight: "#6dd97a",
  orange:     "#f0a020",
  blue:       "#2fa8d5",
  blueLight:  "#5dc8ea",
  navy:       "#0d2240",
  bg:         "#f4faf5",
  white:      "#ffffff",
  text:       "#0d2240",
  muted:      "#5a7a6a",
  border:     "#c8e8cc",
  danger:     "#ef4444",
  primary:    "#4aad52",
  onDark:     "#ffffff",
  onDarkMuted:"rgba(255,255,255,0.75)",
  onDarkHint: "rgba(255,255,255,0.55)",
};

const sendLeadEmail = async (leadData, type) => {
  try {
    const templateParams = {
      to_email: TARGET_EMAIL,
      lead_type: type,
      name: leadData.name,
      email: leadData.email,
      phone: leadData.phone,
      neet_rank: leadData.neetRank || leadData.examScore || leadData.rank || "Not provided",
      college_name: leadData.collegeName || "Not applicable",
      page_url: window.location.href,
      timestamp: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
      user_agent: navigator.userAgent,
      otp_verified: leadData.otpVerified ? "YES" : "SKIPPED"
    };
    await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
    return true;
  } catch (err) {
    console.error("Email send error:", err);
    return false;
  }
};

const openGoogleSearch = (collegeName, leadData) => {
  const searchQuery = encodeURIComponent(`${collegeName} medical college admission cut off ${new Date().getFullYear()}`);
  window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank');
};

/* ─────────────────────────────────────────
   DATA (unchanged)
───────────────────────────────────────── */
const EDVISOR_STATS = [
  { number: "18+", label: "Years of Experience", icon: "📅" },
  { number: "50k+", label: "Students Counselled", icon: "👨‍🎓" },
  { number: "100+", label: "University Partners", icon: "🌍" },
  { number: "30k+", label: "Happy Students", icon: "😊" },
  { number: "93+", label: "Countries Served", icon: "🤝" }
];

const EDVISOR_SERVICES = [
  { title: "Career Counselling", description: "Expert guidance to help you understand your maximum potential.", icon: "🎯" },
  { title: "Counselling Assistance", description: "Customised counselling to explore admission challenges.", icon: "💬" },
  { title: "Course & College Info", description: "In-depth examinations of academic programs.", icon: "📚" },
  { title: "Financial Estimation", description: "Comprehensive financial planning for your education.", icon: "💰" },
  { title: "Study Abroad Assistance", description: "Hassle-free documentation and visa support.", icon: "✈️" },
  { title: "Admission Guidance", description: "Placement assistance for your future career.", icon: "🎓" }
];

const EDVISOR_TESTIMONIALS = [
  { name: "Rohit Sharma", course: "MBBS – Russia", text: "Edvisor and their expert counselors made my MBBS journey to Russia effortless! From university selection to visa and travel, everything was handled professionally. Highly recommended!", avatar: "🩺", rating: 5 },
  { name: "Priya Mehta", course: "MBA – India", text: "Choosing the right MBA program was overwhelming, but the Edvisor counselors provided expert guidance that helped me secure admission in a top-tier business school.", avatar: "🎓", rating: 5 },
  { name: "Ananya Das", course: "BBA – Georgia", text: "Edvisor helped me get into a reputed university in Georgia for my BBA. Their counselors guided me at every step, making the admission process smooth.", avatar: "✈️", rating: 5 },
  { name: "Verified Student", course: "MBBS Admission", text: "They provide detailed guidance for admission. Their counselling session is very effective. They also offer the lowest package and take care of your whole admission process.", avatar: "⭐", rating: 5 }
];

const EDVISOR_COURSES = [
  { icon: "🩺", title: "MBBS", desc: "Top medical colleges in India & abroad" },
  { icon: "🦷", title: "BDS", desc: "Premier dental colleges guidance" },
  { icon: "👩‍⚕️", title: "Nursing", desc: "BSc & GNM nursing programs" },
  { icon: "💼", title: "MBA", desc: "Admission to top business schools" },
  { icon: "⚙️", title: "BTech", desc: "Leading engineering institutions" },
  { icon: "🌿", title: "BAMS / AYUSH", desc: "Ayurvedic & homeopathy colleges" },
  { icon: "💊", title: "Pharmacy", desc: "B.Pharm & M.Pharm guidance" },
  { icon: "🧬", title: "Postgraduate (PG)", desc: "MD / MS / MDS specialized courses" },
];

const MBBS_ABROAD_BENEFITS = [
  { icon: "💰", title: "Affordable Tuition Fees", desc: "Significantly cheaper than private medical colleges in India. Russia, Georgia, Kazakhstan offer quality education at a fraction of the cost." },
  { icon: "🌍", title: "Globally Recognized Degrees", desc: "Edvisor partners with universities recognized by WHO, NMC, and ECFMG — your degree is valid globally." },
  { icon: "📝", title: "No Donation / No Capitation Fee", desc: "Unlike Indian private colleges, many universities abroad offer direct admissions without hefty donations." },
  { icon: "🗣️", title: "English Medium Curriculum", desc: "Most international medical universities offer MBBS in English, eliminating language barriers." },
  { icon: "📊", title: "High FMGE / NExT Pass Rates", desc: "Students placed by Edvisor have an impressive track record in clearing FMGE and the upcoming NExT exam." },
  { icon: "🏥", title: "Extensive Clinical Training", desc: "International universities provide hands-on clinical training equipping students for real-life medical scenarios." },
];

const COUNTRIES_SERVED = [
  "🇷🇺 RUSSIA", "🇧🇩 BANGLADESH", "🇰🇿 KAZAKHSTAN", "🇨🇳 CHINA", "🇳🇵 NEPAL",
  "🇺🇿 UZBEKISTAN", "🇰🇬 KYRGYZSTAN", "🇪🇬 EGYPT", "🇬🇪 GEORGIA"
];

const RANK_THEMES = {
  "1-100":       { color: "#ef4444", name: "🔴 Rank 1–100 (Top Tier – Dream Colleges)", badge: "DREAM",        emoji: "🔴" },
  "100-200":     { color: "#f97316", name: "🟠 Rank 100–200 – Premier Institutes",       badge: "PREMIER",      emoji: "🟠" },
  "200-300":     { color: "#fbbf24", name: "🟡 Rank 200–300 – Elite Colleges",           badge: "ELITE",        emoji: "🟡" },
  "300-400":     { color: "#22c55e", name: "🟢 Rank 300–400 – Top GMCs",                 badge: "TOP",          emoji: "🟢" },
  "400-500":     { color: "#3b82f6", name: "🔵 Rank 400–500 – Strong GMCs",              badge: "STRONG",       emoji: "🔵" },
  "500-600":     { color: "#a855f7", name: "🟣 Rank 500–600 – Mid-Top GMCs",             badge: "MID-TOP",      emoji: "🟣" },
  "600-700":     { color: "#8b5cf6", name: "🟤 Rank 600–700 – Good GMCs",               badge: "GOOD",         emoji: "🟤" },
  "700-800":     { color: "#374151", name: "⚫ Rank 700–800 – Peripheral GMCs",          badge: "PERIPHERAL",   emoji: "⚫" },
  "800-900":     { color: "#6b7280", name: "⚪ Rank 800–900 – Newer GMCs",               badge: "NEW",          emoji: "⚪" },
  "900-1000":    { color: "#10b981", name: "🟩 Rank 900–1000 – Borderline GMCs",         badge: "BORDER",       emoji: "🟩" },
  "1000-2000":   { color: "#ec489a", name: "🎯 Rank 1000–2000 – Strong Government",      badge: "GOVT",         emoji: "🎯" },
  "2000-3000":   { color: "#14b8a6", name: "🟢 Rank 2000–3000 – Strong GMCs + AIIMS",   badge: "STRONG",       emoji: "🟢" },
  "3000-4000":   { color: "#f97316", name: "🟡 Rank 3000–4000 – Top-Mid GMCs",          badge: "TOP-MID",      emoji: "🟡" },
  "4000-5000":   { color: "#84cc16", name: "🟠 Rank 4000–5000 – Mid-Tier GMCs",         badge: "MID-TIER",     emoji: "🟠" },
  "5000-6000":   { color: "#eab308", name: "🔴 Rank 5000–6000 – Good State Colleges",   badge: "STATE",        emoji: "🔴" },
  "6000-7000":   { color: "#a855f7", name: "🟣 Rank 6000–7000 – Newer GMCs",            badge: "NEW-GMC",      emoji: "🟣" },
  "7000-8000":   { color: "#8b5cf6", name: "🟤 Rank 7000–8000 – New Colleges",          badge: "NEW",          emoji: "🟤" },
  "8000-9000":   { color: "#374151", name: "⚫ Rank 8000–9000 – Newer Campuses",         badge: "NEW",          emoji: "⚫" },
  "9000-10000":  { color: "#6b7280", name: "⚪ Rank 9000–10000 – Low-Tier GMC",          badge: "LOW-GMC",      emoji: "⚪" },
  "10000-15000": { color: "#059669", name: "🟢 Rank 10,000–15,000 – Newer GMCs Possible",badge: "NEW-POSSIBLE", emoji: "🟢" },
  "15000-20000": { color: "#d97706", name: "🟡 Rank 15,000–20,000 – Limited Govt",      badge: "LIMITED",      emoji: "🟡" },
  "20000-25000": { color: "#7c3aed", name: "🟠 Rank 20,000–25,000 – Low-Tier Govt",     badge: "LOW-TIER",     emoji: "🟠" },
  "25000-30000": { color: "#db2777", name: "🔴 Rank 25,000–30,000 – Govt Chances ↓",    badge: "CHANCE",       emoji: "🔴" },
  "30000-35000": { color: "#dc2626", name: "🟣 Rank 30,000–35,000 – Mostly Private",    badge: "PRIVATE",      emoji: "🟣" },
  "35000-40000": { color: "#8b5cf6", name: "🟤 Rank 35,000–40,000 – Private MBBS Main", badge: "PRIVATE-MAIN", emoji: "🟤" },
  "40000-45000": { color: "#f59e0b", name: "⚫ Rank 40,000–45,000 – Deemed Universities",badge: "DEEMED",       emoji: "⚫" },
  "45000-50000": { color: "#6366f1", name: "⚪ Rank 45,000–50,000 – Private Zone",       badge: "PRIVATE-ZONE", emoji: "⚪" },
  "50000-60000": { color: "#06b6d4", name: "🟩 Rank 50,000–60,000 – Private Dominant",  badge: "PRIVATE-DOM",  emoji: "🟩" },
  "60000-70000": { color: "#84cc16", name: "🟢 Rank 60,000–70,000 – Mixed Options",     badge: "MIXED",        emoji: "🟢" },
  "70000-80000": { color: "#eab308", name: "🟡 Rank 70,000–80,000 – Private Main",      badge: "PRIVATE-MAIN", emoji: "🟡" },
  "80000-90000": { color: "#ef4444", name: "🟠 Rank 80,000–90,000 – Private + Deemed",  badge: "PRIVATE+DEEMED",emoji: "🟠" },
  "90000-100000":{ color: "#a855f7", name: "🔴 Rank 90,000–1,00,000 – Private + Backups",badge: "BACKUP",      emoji: "🔴" },
  "100000-150000":{ color: "#4b5563",name: "🎯 Rank 1,00,000–1,50,000 – Private + BDS", badge: "BDS-ZONE",    emoji: "🎯" },
  "150000-200000":{ color: "#059669",name: "🟢 Rank 1,50,000–2,00,000 – BDS + AYUSH",  badge: "AYUSH",       emoji: "🟢" },
  "200000-300000":{ color: "#d97706",name: "🟡 Rank 2,00,000–3,00,000 – AYUSH Dominant",badge: "AYUSH-DOM",   emoji: "🟡" },
  "300000-400000":{ color: "#7c3aed",name: "🟠 Rank 3,00,000–4,00,000 – Non-MBBS Fields",badge: "NON-MBBS",   emoji: "🟠" },
  "400000-500000":{ color: "#db2777",name: "🔴 Rank 4,00,000–5,00,000 – Backup Careers",badge: "BACKUP",      emoji: "🔴" },
  "500000-600000":{ color: "#f97316",name: "🟣 Rank 5,00,000–6,00,000 – Alternative Paths",badge: "ALTERNATE", emoji: "🟣" }
};

const RANK_WISE_COLLEGES = [
  { rankRange: "1-100", minRank: 1, maxRank: 100, note: "AIIMS Delhi usually closes within top 50 ranks", colleges: ["AIIMS Delhi","JIPMER Puducherry","Maulana Azad Medical College (Delhi)","VMMC & Safdarjung (Delhi)","Seth GS Medical College (Mumbai)","AIIMS Bhubaneswar","AIIMS Jodhpur","AIIMS Bhopal","King George's Medical University (Lucknow)","Grant Medical College (Mumbai)"] },
  { rankRange: "100-200", minRank: 100, maxRank: 200, note: "", colleges: ["Lady Hardinge Medical College (Delhi)","Madras Medical College (Chennai)","Bangalore Medical College","IMS BHU (Varanasi)","SMS Medical College (Jaipur)","Government Medical College Chandigarh","AIIMS Rishikesh","AIIMS Patna","AIIMS Raipur","BJ Medical College Pune"] },
  { rankRange: "200-300", minRank: 200, maxRank: 300, note: "", colleges: ["AIIMS Nagpur","AIIMS Mangalagiri","AIIMS Gorakhpur","AIIMS Kalyani","Government Medical College Thiruvananthapuram","GMC Nagpur","Osmania Medical College","Andhra Medical College","JLN Medical College (Ajmer)","Stanley Medical College"] },
  { rankRange: "300-400", minRank: 300, maxRank: 400, note: "", colleges: ["AIIMS Bathinda","AIIMS Deoghar","AIIMS Rajkot","AIIMS Bibinagar","Government Medical College Surat","Topiwala National Medical College (Mumbai)","ESIC Medical College (various)","KGMU (lower quota seats)","GMC Baroda","GMC Patiala"] },
  { rankRange: "400-500", minRank: 400, maxRank: 500, note: "", colleges: ["AIIMS Bilaspur","AIIMS Guwahati","AIIMS Jammu (Vijaypur)","Government Medical College Kozhikode","GMC Amritsar","GMC Bhopal","GMC Indore","GMC Ranchi","GMC Raipur","GMC Aurangabad"] },
  { rankRange: "500-600", minRank: 500, maxRank: 600, note: "", colleges: ["AIIMS (newer campuses)","Government Medical College Jammu","GMC Srinagar","GMC Trichy","GMC Salem","GMC Mysore","GMC Kolhapur","GMC Guntur","GMC Kurnool","GMC Bellary"] },
  { rankRange: "600-700", minRank: 600, maxRank: 700, note: "", colleges: ["GMC Kota","GMC Udaipur","GMC Patna","Nalanda Medical College","GMC Dibrugarh","GMC Silchar","GMC Haldwani","GMC Dehradun","GMC Goa","GMC Agartala"] },
  { rankRange: "700-800", minRank: 700, maxRank: 800, note: "Peripheral GMCs with state quota advantage", colleges: ["Peripheral GMCs (state quota advantage)","ESIC Colleges (Pan India)","Newly opened GMCs","North-East GMCs","UP/MP newer colleges","Bihar newer GMCs","Rajasthan newer GMCs","Maharashtra semi-top GMCs","Telangana newer GMCs","Tamil Nadu peripheral GMCs"] },
  { rankRange: "800-900", minRank: 800, maxRank: 900, note: "", colleges: ["New AIIMS (borderline chances)","Lower GMCs (state quota heavy)","Semi-government colleges","ESIC colleges","Private (low fee) deemed universities","Rural govt colleges","Bond-heavy colleges","Newly established MBBS colleges","Dental (BDS fallback top colleges)","AYUSH top colleges (backup)"] },
  { rankRange: "900-1000", minRank: 900, maxRank: 1000, note: "", colleges: ["Newest AIIMS (rare chance)","Low-tier GMCs (mostly state quota)","Private deemed colleges (good infra)","Semi-govt colleges","ESIC last seats","North-East GMCs","Bond colleges","Peripheral state colleges","BDS top govt colleges","Private MBBS backup"] },
  { rankRange: "1000-2000", minRank: 1000, maxRank: 2000, note: "Top govt colleges still possible", colleges: ["AIIMS Raebareli","AIIMS Kalyani","AIIMS Bibinagar (Hyderabad)","AIIMS Gorakhpur","AIIMS Rajkot","AIIMS Bilaspur","Government Medical College Kottayam","SCB Medical College Cuttack","Grant Medical College Mumbai","BJ Medical College Ahmedabad","Patna Medical College","Indira Gandhi Medical College Shimla"] },
  { rankRange: "2000-3000", minRank: 2000, maxRank: 3000, note: "Strong GMCs + AIIMS lower branches", colleges: ["Patna Medical College","Indira Gandhi Medical College Shimla","Gandhi Medical College Secunderabad","RUHS Medical College Jaipur","Government Medical College Nagpur","Government Medical College Trivandrum (lower quota)","SMS Medical College Jaipur (lower categories)","Osmania Medical College","Andhra Medical College","Medical College Kolkata"] },
  { rankRange: "3000-4000", minRank: 3000, maxRank: 4000, note: "Top-mid govt colleges", colleges: ["Government Medical College Surat","Government Medical College Baroda","Government Medical College Amritsar","Government Medical College Patiala","Government Medical College Indore","Government Medical College Bhopal","Government Medical College Ranchi","Government Medical College Raipur","Government Medical College Aurangabad","ESIC Medical Colleges (various campuses)"] },
  { rankRange: "4000-5000", minRank: 4000, maxRank: 5000, note: "Mid-tier govt colleges", colleges: ["Government Medical College Jammu","Government Medical College Srinagar","Government Medical College Kozhikode","Government Medical College Mysore","Government Medical College Salem","Government Medical College Kolhapur","Government Medical College Guntur","Government Medical College Kurnool","Government Medical College Bellary","Government Medical College Goa"] },
  { rankRange: "5000-6000", minRank: 5000, maxRank: 6000, note: "Good state colleges + peripheral GMCs", colleges: ["Government Medical College Kota","Government Medical College Udaipur","Government Medical College Patna (state quota)","Nalanda Medical College","Government Medical College Dibrugarh","Government Medical College Silchar","Government Medical College Haldwani","Government Medical College Dehradun","Government Medical College Agartala","Government Medical College Imphal"] },
  { rankRange: "6000-7000", minRank: 6000, maxRank: 7000, note: "Newer GMCs start dominating", colleges: ["Government Medical College Churu","Government Medical College Pali","Government Medical College Shahdol","Government Medical College Vidisha","Government Medical College Ratlam","Government Medical College Satna","Government Medical College Nanded","Government Medical College Yavatmal","Government Medical College Latur","ESIC Medical Colleges (remaining seats)"] },
  { rankRange: "7000-8000", minRank: 7000, maxRank: 8000, note: "New colleges + state advantage", colleges: ["Government Medical College Ambikapur","Government Medical College Korba","Government Medical College Rajnandgaon","Government Medical College Chhindwara","Government Medical College Datia","Government Medical College Sikar","Government Medical College Barmer","Government Medical College Dausa","Government Medical College Jhunjhunu","Government Medical College Karauli"] },
  { rankRange: "8000-9000", minRank: 8000, maxRank: 9000, note: "Almost fully newer colleges", colleges: ["Government Medical College Nandurbar","Government Medical College Gondia","Government Medical College Chandrapur","Government Medical College Hingoli","Government Medical College Palghar","Government Medical College Jalna","Government Medical College Osmanabad","Government Medical College Basti","Government Medical College Ayodhya","Government Medical College Firozabad"] },
  { rankRange: "9000-10000", minRank: 9000, maxRank: 10000, note: "Low-tier GMC + private backup begins", colleges: ["Government Medical College Siddipet","Government Medical College Nizamabad","Government Medical College Mahabubnagar","Government Medical College Eluru","Government Medical College Vizianagaram","Government Medical College Rajamahendravaram","Government Medical College Nandyal","Government Medical College Machilipatnam","Government Medical College Khammam","Government Medical College Kamareddy"] },
  { rankRange: "10000-15000", minRank: 10000, maxRank: 15000, note: "Govt MBBS still possible – newer colleges", colleges: ["Government Medical College Thiruvarur","Government Medical College Kathua","Government Medical College Faizabad (Ayodhya)","Government Medical College Sirohi","Government Medical College Firozabad","Government Medical College Khandwa","Government Medical College Ratlam","Government Medical College Basti","Government Medical College Badaun","Shri Bhausaheb Hire GMC Dhule"] },
  { rankRange: "15000-20000", minRank: 15000, maxRank: 20000, note: "Govt MBBS possible but limited + heavy state quota", colleges: ["Government Medical College Nanded","Government Medical College Chandrapur","Government Medical College Jagdalpur","Government Medical College Udhampur","Government Medical College Shivpuri","Government Medical College Yavatmal","Government Medical College Nellore (ACSR)","Government Medical College Bundi","Diamond Harbour Government Medical College","Government Medical College Pudukkottai"] },
  { rankRange: "20000-25000", minRank: 20000, maxRank: 25000, note: "Mix: low-tier govt + semi-govt + ESIC", colleges: ["ESIC Medical College Gulbarga","ESIC Medical College Faridabad","Government Medical College Hazaribagh","Government Medical College Palamu","Government Medical College Dungarpur","Government Medical College Baramulla","Government Medical College Rajouri","Government Medical College Chamba","Government Medical College Hamirpur","Government Medical College Nahan"] },
  { rankRange: "25000-30000", minRank: 25000, maxRank: 30000, note: "Govt chances ↓, semi/private start", colleges: ["Government Medical College Ambedkar Nagar","Government Medical College Kannauj","Government Medical College Jalaun","Government Medical College Azamgarh","Government Medical College Saharanpur","Government Medical College Madhepura","Government Medical College Bettiah","Government Medical College Purnea","Government Medical College Darbhanga (low quota seats)","ESIC + state quota leftover colleges"] },
  { rankRange: "30000-35000", minRank: 30000, maxRank: 35000, note: "Mostly private + few govt via quota", colleges: ["Government Medical College Karur (TN state quota)","Government Medical College Ariyalur","Government Medical College Nagapattinam","Government Medical College Ramanathapuram","Government Medical College Tiruppur","Government Medical College Virudhunagar","Semi-govt colleges Gujarat","ESIC last-round colleges","Deemed universities (low fee tier)","BDS top govt colleges"] },
  { rankRange: "35000-40000", minRank: 35000, maxRank: 40000, note: "Private MBBS becomes main option", colleges: ["KS Hegde Medical Academy (Mangalore)","JSS Medical College (Mysore)","SRM Medical College (Chennai)","DY Patil Medical College (Kolhapur)","Bharati Vidyapeeth Medical College (Pune)","MGM Medical College (Aurangabad)","Kalinga Institute of Medical Sciences (Bhubaneswar)","Amrita Institute of Medical Sciences (Kochi)","Hamdard Institute of Medical Sciences (Delhi)","Saveetha Medical College (Chennai)"] },
  { rankRange: "40000-45000", minRank: 40000, maxRank: 45000, note: "Private + deemed universities dominate", colleges: ["Rama Medical College (Kanpur)","Rohilkhand Medical College (Bareilly)","Heritage Institute of Medical Sciences (Varanasi)","Sharda Medical College (Greater Noida)","Noida International Institute of Medical Sciences","Teerthanker Mahaveer Medical College","Santosh Medical College (Ghaziabad)","Era's Lucknow Medical College","Integral Medical College (Lucknow)","Subharti Medical College (Meerut)"] },
  { rankRange: "45000-50000", minRank: 45000, maxRank: 50000, note: "Private MBBS / BDS / AYUSH main zone", colleges: ["Private colleges UP (many options)","Private colleges Telangana","Private colleges Karnataka","Private colleges Tamil Nadu","Private colleges Maharashtra","Minority/private colleges Kerala","Deemed universities (high fees)","BDS govt colleges","AYUSH colleges (BAMS/BHMS)","Abroad MBBS (Russia, Georgia etc.)"] },
  { rankRange: "50000-60000", minRank: 50000, maxRank: 60000, note: "Private MBBS dominant + rare govt via quota", colleges: ["Guntur Medical College","Pt. BD Sharma PGIMS Rohtak","JSS Medical College Mysore","Jawaharlal Nehru Medical College Ajmer","Rajendra Institute of Medical Sciences Ranchi","Sri Krishna Medical College Muzaffarpur","Government Medical College Banda","Bundelkhand Medical College Sagar","Kurnool Medical College","Government Medical College Akola"] },
  { rankRange: "60000-70000", minRank: 60000, maxRank: 70000, note: "Mixed: private + few govt edge cases", colleges: ["Moti Lal Nehru Medical College Allahabad","Stanley Medical College Chennai (reserved quota)","Rajkiya Medical College Jalaun","Chhindwara Institute of Medical Sciences","SLBS Government Medical College Mandi","Government Medical College Baroda (reserved seats)","Gandhi Medical College Bhopal (category quota)","Hamdard Institute of Medical Sciences Delhi","Manipal Tata Medical College Jamshedpur","Bharati Vidyapeeth Medical College Pune"] },
  { rankRange: "70000-80000", minRank: 70000, maxRank: 80000, note: "Private MBBS becomes main option", colleges: ["KS Hegde Medical Academy Mangalore","SRM Medical College Chennai","DY Patil Medical College Pune","DY Patil Medical College Navi Mumbai","Jawaharlal Nehru Medical College Wardha","Datta Meghe Medical College Nagpur","Sri Siddhartha Medical College Bangalore","Saveetha Medical College Chennai","Kalinga Institute of Medical Sciences Bhubaneswar","Amrita Institute of Medical Sciences Kochi"] },
  { rankRange: "80000-90000", minRank: 80000, maxRank: 90000, note: "Private + deemed universities", colleges: ["Rama Medical College Kanpur","Rohilkhand Medical College Bareilly","Heritage Institute of Medical Sciences Varanasi","Sharda Medical College Greater Noida","Noida International Institute of Medical Sciences","Teerthanker Mahaveer Medical College Moradabad","Santosh Medical College Ghaziabad","Era's Lucknow Medical College","Integral Medical College Lucknow","Subharti Medical College Meerut"] },
  { rankRange: "90000-100000", minRank: 90000, maxRank: 100000, note: "Private MBBS + backups strongly needed", colleges: ["Private colleges in Uttar Pradesh","Private colleges in Karnataka","Private colleges in Telangana","Private colleges in Tamil Nadu","Private colleges in Maharashtra","Minority/private colleges Kerala","Deemed universities (high fees tier)","BDS govt colleges (good fallback)","BAMS / BHMS colleges","MBBS abroad (Russia, Georgia, Kazakhstan)"] },
  { rankRange: "100000-150000", minRank: 100000, maxRank: 150000, note: "Private MBBS + BDS + AYUSH", colleges: ["Sharda Medical College (Greater Noida)","Santosh Medical College (Ghaziabad)","Subharti Medical College (Meerut)","Era's Lucknow Medical College","Integral Medical College (Lucknow)","Teerthanker Mahaveer Medical College (Moradabad)","Rama Medical College (Kanpur)","Rohilkhand Medical College (Bareilly)","Private BDS colleges (Govt dental also possible)","BAMS colleges (top state colleges)"] },
  { rankRange: "150000-200000", minRank: 150000, maxRank: 200000, note: "Mostly BDS + AYUSH + private MBBS rare", colleges: ["Private MBBS colleges (management quota seats)","BDS – Government Dental Colleges","BDS – Private colleges","BAMS (Ayurveda colleges – UP, Bihar, MP)","BHMS (Homeopathy colleges)","BUMS colleges","Veterinary colleges (BVSc)","Nursing (BSc Nursing top institutes)","Allied health sciences","Paramedical colleges"] },
  { rankRange: "200000-300000", minRank: 200000, maxRank: 300000, note: "AYUSH + BDS dominate", colleges: ["BAMS colleges (Govt + private)","BHMS colleges","BUMS colleges","Private BDS colleges","Veterinary (BVSc colleges)","Agriculture universities (BSc Agri)","Pharmacy colleges (B.Pharm)","Physiotherapy (BPT) colleges","Nursing colleges","Paramedical courses"] },
  { rankRange: "300000-400000", minRank: 300000, maxRank: 400000, note: "Non-MBBS medical fields", colleges: ["Private BAMS colleges","Private BHMS colleges","BDS (low-tier private)","Nursing (BSc Nursing)","Physiotherapy (BPT)","Occupational Therapy","Pharmacy (B.Pharm)","Biotechnology courses","Microbiology courses","Allied health sciences"] },
  { rankRange: "400000-500000", minRank: 400000, maxRank: 500000, note: "Backup medical careers", colleges: ["BAMS (private colleges)","BHMS (private)","BUMS colleges","BSc Nursing","BPT (Physiotherapy)","Pharmacy colleges","Paramedical diplomas","Lab technician courses","Radiology technician","Dialysis technician"] },
  { rankRange: "500000-600000", minRank: 500000, maxRank: 600000, note: "Shift away from core MBBS path", colleges: ["AYUSH colleges (private)","Nursing colleges","Pharmacy colleges","BSc Biology","Biotechnology","Microbiology","Food technology","Environmental science","Agriculture colleges","Fisheries science"] }
];

const getCollegesForRank = (rank) => {
  for (const range of RANK_WISE_COLLEGES) {
    if (rank >= range.minRank && rank <= range.maxRank) {
      return { rangeData: range, colleges: range.colleges, note: range.note };
    }
  }
  return { rangeData: { rankRange: "600000+", minRank: 600000, maxRank: Infinity, colleges: [], note: "" }, colleges: ["BSc Nursing (private)","Pharmacy (B.Pharm)","BSc Biotechnology","Paramedical courses"], note: "Explore alternative healthcare careers like Nursing, Pharmacy, Physiotherapy." };
};

const getThemeForRank = (rank) => {
  for (const [range, theme] of Object.entries(RANK_THEMES)) {
    const [min, max] = range.split('-').map(Number);
    if (rank >= min && rank <= max) return theme;
  }
  return { color: "#6b7280", name: "Alternative Career Paths", badge: "ALTERNATIVE", emoji: "📚" };
};

/* ─────────────────────────────────────────
   SHARED STYLE CONSTANTS
───────────────────────────────────────── */
const modalOverlayStyle = {
  position:"fixed", top:0, left:0, right:0, bottom:0,
  background:"rgba(0,0,0,0.7)", backdropFilter:"blur(5px)",
  display:"flex", alignItems:"center", justifyContent:"center",
  zIndex:10000, fontFamily:"'Poppins',sans-serif"
};
const modalContainerStyle = {
  background:S.white, borderRadius:24, maxWidth:450, width:"90%",
  boxShadow:"0 20px 40px rgba(0,0,0,0.3)", overflow:"hidden",
  animation:"edv-fadeIn 0.3s ease"
};
const modalHeaderStyle = {
  background:`linear-gradient(135deg,${S.navy},#1a3a6c)`,
  padding:"20px 24px", display:"flex", justifyContent:"space-between", alignItems:"center"
};
const closeButtonStyle = { background:"none", border:"none", color:"#fff", fontSize:28, cursor:"pointer", lineHeight:1, padding:"0 8px" };
const labelStyle = { fontSize:13, fontWeight:600, color:S.text, display:"block", marginBottom:6 };
const inputStyle = (err) => ({
  width:"100%", border:`1.5px solid ${err?S.danger:S.border}`, borderRadius:12,
  padding:"12px 14px", fontSize:14, outline:"none", fontFamily:"inherit",
  background:"#f9fafb", color:S.text
});
const errorStyle = { color:S.danger, fontSize:11, marginTop:4 };

/* ─────────────────────────────────────────
   LAYOUT COMPONENTS (unchanged)
───────────────────────────────────────── */
function Topbar({ onCounsellingClick, onHelpClick, onAboutClick }) {
  return (
    <div style={{ background:S.navy, color:"#a8c8d0", fontSize:"clamp(11px,3vw,12px)", padding:"6px 0" }}>
      <div style={{ maxWidth:1200, margin:"auto", padding:"0 20px", display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:6 }} className="topbar-content">
        <span className="topbar-left" style={{ color:"#a8c8d0", display:"flex", flexWrap:"wrap", gap:6 }}>
          <span className="desktop-only-text">🎓 EdVisor NEET 2026 College Predictor is Live! &nbsp;|&nbsp;</span>
          <span className="mobile-only-text" style={{ display:"none" }}>🎓 NEET 2026 Predictor |</span>
          <button onClick={onCounsellingClick} style={{ background:"none", border:"none", color:"#7ee8a0", cursor:"pointer", textDecoration:"underline", font:"inherit" }}>Get Free Counselling</button>
        </span>
        <span>
          <button onClick={onHelpClick} style={{ background:"none", border:"none", color:"#7ec8e8", cursor:"pointer", textDecoration:"underline", font:"inherit" }}>Help</button>
          &nbsp;|&nbsp;
          <button onClick={onAboutClick} style={{ background:"none", border:"none", color:"#7ec8e8", cursor:"pointer", textDecoration:"underline", font:"inherit" }}>About EdVisor</button>
        </span>
      </div>
    </div>
  );
}

function Navbar() {
  return (
    <nav style={{ background:S.white, boxShadow:"0 2px 12px rgba(0,0,0,0.09)", position:"sticky", top:0, zIndex:100 }}>
      <div style={{ maxWidth:1200, margin:"auto", padding:"0 16px", display:"flex", alignItems:"center", flexWrap:"wrap", minHeight:62, gap:12 }}>
        <a href="#" style={{ display:"flex", alignItems:"center", gap:8, textDecoration:"none", flexShrink:0 }}>
          <img src={Edvisor_Logo_main} alt="EdVisor Logo" style={{ height:50, width:"auto" }} />
        </a>
        <div style={{ display:"flex", gap:6, marginLeft:"auto", flexWrap:"wrap" }} className="nav-links desktop-nav">
          {["Rank Predictor","Study Abroad","MBBS Abroad"].map(l => (
            <a key={l} href="#" style={{ textDecoration:"none", color:S.text, fontSize:"clamp(12px,3.5vw,13.5px)", fontWeight:600, padding:"6px 10px", borderRadius:8 }}>{l}</a>
          ))}
        </div>
      </div>
    </nav>
  );
}

function Marquee() {
  const items = ["📢 NEET UG 2026 Results Announced!","🏥 Top Medical Colleges 2026 Rankings Released →","🎓 NEET College Predictor 2026 — Free","AIIMS Cutoffs Updated for 2026 →","🔬 MCC Counselling Schedule Released","🌍 Study MBBS Abroad - Russia, Bangladesh, Nepal, Georgia","📞 Free Career Counselling - Call Now!"];
  const all = [...items, ...items];
  return (
    <div style={{ background:S.navy, color:"#7ec8e8", padding:"8px 0", overflow:"hidden" }}>
      <div style={{ display:"flex", gap:50, animation:"edv-marquee 30s linear infinite", whiteSpace:"nowrap", width:"max-content" }}>
        {all.map((item,i) => <span key={i} style={{ fontSize:"clamp(11px,3vw,12.5px)", color:"#7ec8e8" }} className="marquee-text">{item} &nbsp;|&nbsp;</span>)}
      </div>
    </div>
  );
}

function PredictBtn({ onClick, children, style={}, disabled=false }) {
  const [hover,setHover] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)} disabled={disabled}
      className="predict-btn"
      style={{ width:"100%", padding:"clamp(12px,4vw,15px) 20px", background:disabled?"#aaa":`linear-gradient(90deg,${S.greenDark} 0%,${S.green} 40%,${S.greenLight} 60%,${S.blue} 100%)`, color:"#fff", border:"none", borderRadius:14, fontSize:"clamp(13px,4vw,15px)", fontWeight:700, fontFamily:"inherit", cursor:disabled?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, boxShadow:hover&&!disabled?`0 6px 28px rgba(74,173,82,0.55)`:`0 4px 20px rgba(74,173,82,0.4)`, transform:hover&&!disabled?"translateY(-2px)":"translateY(0)", transition:"all .15s", ...style }}>
      {children}
    </button>
  );
}

function SectionCard({ children, style={} }) {
  return (
    <div style={{ background:S.white, borderRadius:16, border:`1.5px solid ${S.border}`, padding:"clamp(20px,5vw,26px) clamp(16px,4vw,28px)", boxShadow:"0 2px 12px rgba(0,0,0,0.05)", ...style }} className="section-padding">
      {children}
    </div>
  );
}

function SectionTitle({ icon, children, lightMode=false }) {
  return (
    <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:"clamp(16px,4vw,17px)", fontWeight:800, color: lightMode ? "#fff" : S.navy, marginBottom:20, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
      <div style={{ width:32, height:32, background: lightMode ? "rgba(255,255,255,0.2)" : "linear-gradient(135deg,#d8f5dc,#b2e8ba)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>{icon}</div>
      {children}
    </div>
  );
}

function Spinner({ show }) {
  if (!show) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(10,26,46,0.55)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", backdropFilter:"blur(4px)" }}>
      <div style={{ width:56, height:56, border:"4px solid rgba(255,255,255,0.2)", borderTop:`4px solid ${S.green}`, borderRadius:"50%", animation:"edv-spin 0.8s linear infinite" }} />
      <div style={{ color:"#fff", marginTop:16, fontSize:"clamp(14px,4vw,15px)", fontWeight:600 }}>Finding colleges for your rank…</div>
    </div>
  );
}

/* ─────────────────────────────────────────
   OTP MODAL (unchanged – used for college details and counselling)
───────────────────────────────────────── */
function OTPVerificationModal({ isOpen, onClose, onVerify, phoneNumber, actionType }) {
  const [otp, setOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [errors, setErrors] = useState({});
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (isOpen && phoneNumber && !otpSent) sendOTP();
    if (!isOpen) { setOtp(''); setOtpSent(false); setErrors({}); setResendDisabled(false); setCountdown(0); }
  }, [isOpen, phoneNumber]);

  useEffect(() => {
    let timer;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    else if (countdown === 0 && resendDisabled) setResendDisabled(false);
    return () => clearTimeout(timer);
  }, [countdown, resendDisabled]);

  const generateRandomOTP = () => Math.floor(100000 + Math.random() * 900000).toString();
  const sendOTP = async () => {
    setIsSendingOtp(true);
    const newOtp = generateRandomOTP();
    setGeneratedOtp(newOtp);
    setTimeout(() => {
      setIsSendingOtp(false); setOtpSent(true); setResendDisabled(true); setCountdown(30);
      alert(`Demo: OTP ${newOtp} sent to ${phoneNumber}. For testing, use OTP: ${newOtp}`);
    }, 1000);
  };
  const handleResendOTP = () => { if (!resendDisabled) sendOTP(); };
  const handleVerify = () => {
    if (!otp) { setErrors({ otp: 'Please enter the OTP' }); return; }
    if (otp !== generatedOtp) { setErrors({ otp: 'Invalid OTP. Please try again.' }); return; }
    setIsVerifying(true);
    setTimeout(() => { setIsVerifying(false); onVerify(true); onClose(); }, 1000);
  };

  if (!isOpen) return null;
  return (
    <div style={modalOverlayStyle}>
      <div style={modalContainerStyle} className="modal-container">
        <div style={modalHeaderStyle} className="modal-header">
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:"#fff" }}>🔐 Verify Your Mobile Number</div>
            <div style={{ fontSize:12, color:"#a8e8b0", marginTop:4 }}>
              {actionType === 'predictor' ? 'To view college results' : actionType === 'college' ? 'To view college details' : 'To request counselling'}
            </div>
          </div>
          <button onClick={onClose} style={closeButtonStyle}>&times;</button>
        </div>
        <div style={{ padding:'24px', background:S.white }} className="modal-body">
          <div style={{ textAlign:'center', marginBottom:20 }}>
            <div style={{ width:60, height:60, background:`${S.green}15`, borderRadius:30, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px', fontSize:28 }}>📱</div>
            <div style={{ fontSize:14, color:S.muted, marginBottom:4 }}>We've sent a verification code to</div>
            <div style={{ fontSize:18, fontWeight:700, color:S.green }}>{phoneNumber}</div>
          </div>
          <div style={{ marginBottom:20 }}>
            <label style={labelStyle}>Enter OTP <span style={{ color:S.danger }}>*</span></label>
            <input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="Enter 6-digit OTP" style={inputStyle(errors.otp)} maxLength={6} autoFocus />
            {errors.otp && <div style={errorStyle}>{errors.otp}</div>}
          </div>
          <PredictBtn onClick={handleVerify} style={{ padding:'12px', fontSize:14 }} disabled={isVerifying}>{isVerifying ? 'Verifying...' : '✅ Verify & Continue'}</PredictBtn>
          <div style={{ textAlign:'center', marginTop:16, fontSize:12, color:S.muted }}>
            {isSendingOtp ? <span>Sending OTP...</span> : otpSent ? (
              <span>Didn't receive OTP? <button onClick={handleResendOTP} disabled={resendDisabled} style={{ background:'none', border:'none', color:resendDisabled?S.muted:S.green, cursor:resendDisabled?'not-allowed':'pointer', fontWeight:600, textDecoration:'underline', font:'inherit' }}>Resend {resendDisabled && `(${countdown}s)`}</button></span>
            ) : <span>Sending OTP...</span>}
          </div>
          <p style={{ fontSize:11, color:S.muted, textAlign:'center', marginTop:20 }}>
            By verifying, you agree to receive updates. All leads sent to <strong style={{ color:S.text }}>dataads@edvisor.co.in</strong>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   COLLEGE DETAILS MODAL (RESTORED – original version)
───────────────────────────────────────── */
function CollegeDetailsModal({ isOpen, onClose, onSubmit, collegeName }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [examScore, setExamScore] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [pendingLeadData, setPendingLeadData] = useState(null);
  const [skipOtp, setSkipOtp] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => { if (!isOpen) { setName(''); setEmail(''); setPhone(''); setExamScore(''); setErrors({}); setShowOtpModal(false); setPendingLeadData(null); setSkipOtp(false); setVerified(false); } }, [isOpen]);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Full name is required';
    if (!email.trim()) e.email = 'Email is required'; else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Email is invalid';
    if (!phone.trim()) e.phone = 'Phone number is required'; else if (!/^\d{10}$/.test(phone.replace(/\D/g,''))) e.phone = 'Enter a valid 10-digit phone number';
    if (!examScore.trim()) e.examScore = 'Please enter your NEET rank/score';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const leadData = { name, email, phone, examScore, neetRank:examScore, collegeName, type:"college_details_request" };
    if (skipOtp) { setIsSubmitting(true); sendLeadEmail({ ...leadData, otpVerified:false }, "College Details Request (OTP Skipped)").then(() => { setIsSubmitting(false); onSubmit(leadData); onClose(); }); }
    else if (verified) { setIsSubmitting(true); sendLeadEmail({ ...leadData, otpVerified:true }, "College Details Request (OTP Verified)").then(() => { setIsSubmitting(false); onSubmit(leadData); onClose(); }); }
    else { setPendingLeadData(leadData); setShowOtpModal(true); }
  };
  const handleOTPVerified = async (verifiedStatus) => {
    if (verifiedStatus && pendingLeadData) { setVerified(true); setIsSubmitting(true); await sendLeadEmail({ ...pendingLeadData, otpVerified:true }, "College Details Request (OTP Verified)"); setIsSubmitting(false); onSubmit(pendingLeadData); onClose(); }
    setShowOtpModal(false); setPendingLeadData(null);
  };

  if (!isOpen) return null;
  return (
    <>
      <OTPVerificationModal isOpen={showOtpModal} onClose={() => setShowOtpModal(false)} onVerify={handleOTPVerified} phoneNumber={phone} actionType="college" />
      <div style={modalOverlayStyle}>
        <div style={modalContainerStyle} className="modal-container">
          <div style={modalHeaderStyle} className="modal-header">
            <div>
              <div style={{ fontSize:20, fontWeight:800, color:"#fff" }}>🔍 Get College Details</div>
              <div style={{ fontSize:12, color:"#a8e8b0", marginTop:4 }}>{collegeName}</div>
            </div>
            <button onClick={onClose} style={closeButtonStyle}>&times;</button>
          </div>
          <form onSubmit={handleSubmit} style={{ padding:'24px', background:S.white }} className="modal-body">
            <div style={{ marginBottom:16 }}>
              <label style={labelStyle}>Full Name <span style={{ color:S.danger }}>*</span></label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Priya Sharma" style={inputStyle(errors.name)} />
              {errors.name && <div style={errorStyle}>{errors.name}</div>}
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={labelStyle}>Email Address <span style={{ color:S.danger }}>*</span></label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="priya@example.com" style={inputStyle(errors.email)} />
              {errors.email && <div style={errorStyle}>{errors.email}</div>}
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={labelStyle}>Phone Number <span style={{ color:S.danger }}>*</span></label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="9876543210" style={inputStyle(errors.phone)} />
              {errors.phone && <div style={errorStyle}>{errors.phone}</div>}
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={labelStyle}>NEET Rank / Score <span style={{ color:S.danger }}>*</span></label>
              <input type="text" value={examScore} onChange={e => setExamScore(e.target.value)} placeholder="e.g. 4500" style={inputStyle(errors.examScore)} />
              {errors.examScore && <div style={errorStyle}>{errors.examScore}</div>}
            </div>
            <div style={{ marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
              <input type="checkbox" id="skipOtpCollege" checked={skipOtp} onChange={e => setSkipOtp(e.target.checked)} />
              <label htmlFor="skipOtpCollege" style={{ fontSize:12, color:S.muted }}>Skip OTP verification</label>
            </div>
            {verified && !skipOtp && <div style={{ color:S.green, fontSize:12, marginBottom:12 }}>✓ Phone number verified</div>}
            <PredictBtn onClick={() => {}} style={{ padding:'12px', fontSize:14 }} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : (skipOtp ? '🔍 View College Details' : (verified ? '🔍 View College Details' : '📱 Send OTP & Continue'))}
            </PredictBtn>
            <p style={{ fontSize:11, color:S.muted, textAlign:'center', marginTop:16 }}>
              By submitting, you agree to receive college updates. All leads sent to <strong style={{ color:S.text }}>dataads@edvisor.co.in</strong>
            </p>
          </form>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────
   UPDATED PREDICTION MODAL (inline OTP + Rebliss SMS)
───────────────────────────────────────── */
function PredictionModal({ isOpen, onClose, onSubmit, rankValue }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [errors, setErrors] = useState({});
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, resendDisabled]);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setEmail('');
      setPhone('');
      setOtp('');
      setGeneratedOtp('');
      setErrors({});
      setIsSendingOtp(false);
      setOtpSent(false);
      setVerified(false);
      setCountdown(0);
      setResendDisabled(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);
    if (!cleanPhone || cleanPhone.length !== 10) newErrors.phone = 'Enter a valid 10-digit phone number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);
    if (!cleanPhone || cleanPhone.length !== 10) {
      setErrors({ phone: 'Enter a valid 10-digit phone number' });
      return;
    }
    setIsSendingOtp(true);
    const newOtp = generateOTP();
    setGeneratedOtp(newOtp);
    const result = await sendOTPviaRebliss(cleanPhone, newOtp);
    if (result.success) {
      setOtpSent(true);
      setCountdown(60);
      setResendDisabled(true);
      setErrors({});
    } else {
      setErrors({ phone: result.message });
      alert(`Failed to send OTP: ${result.message}`);
    }
    setIsSendingOtp(false);
  };

  const handleVerifyOtp = () => {
    if (!otp || otp.length !== 6) {
      setErrors({ otp: 'Enter valid 6-digit OTP' });
      return;
    }
    if (otp === generatedOtp) {
      setVerified(true);
      setErrors({});
      alert('Phone number verified successfully!');
    } else {
      setErrors({ otp: 'Invalid OTP. Please try again.' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!verified) {
      setErrors({ phone: 'Please verify your phone number via OTP first.' });
      alert('Please click "Send OTP" and verify your number first');
      return;
    }
    setIsSubmitting(true);
    const leadData = {
      name,
      email,
      phone,
      rank: rankValue,
      otpVerified: true,
      neetRank: rankValue
    };
    await sendLeadEmail(leadData, "College Predictor Request (OTP Verified)");
    onSubmit(leadData);
    onClose();
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContainerStyle} className="modal-container">
        {/* Header without gradient – matches image */}
        <div style={{ padding: "28px 24px 12px 24px", borderBottom: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: "clamp(20px,5vw,24px)", fontWeight: 800, color: "#0d2240", fontFamily: "'Nunito', sans-serif" }}>Get Your College Matches</div>
            <div style={{ fontSize: "clamp(12px,4vw,13px)", color: "#5a7a6a", marginTop: 4 }}>Free & instant results</div>
          </div>
          <button onClick={onClose} style={{ ...closeButtonStyle, color: "#0d2240" }}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "8px 24px 32px 24px" }}>
          {/* Full Name */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Full Name <span style={{ color: S.danger }}>*</span></label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Priya Sharma"
              style={inputStyle(errors.name)}
            />
            {errors.name && <div style={errorStyle}>{errors.name}</div>}
          </div>

          {/* Email Address */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Email Address <span style={{ color: S.danger }}>*</span></label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="priya@example.com"
              style={inputStyle(errors.email)}
            />
            {errors.email && <div style={errorStyle}>{errors.email}</div>}
          </div>

          {/* Phone Number with inline Send OTP */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Phone Number <span style={{ color: S.danger }}>*</span></label>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="9876543210"
                style={{ ...inputStyle(errors.phone), flex: 1 }}
              />
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={isSendingOtp || (otpSent && !verified && countdown > 0)}
                style={{
                  padding: "0 20px",
                  borderRadius: 12,
                  border: "none",
                  background: (isSendingOtp || (otpSent && !verified && countdown > 0)) ? "#ccc" : S.blue,
                  color: "#fff",
                  fontWeight: 600,
                  cursor: (isSendingOtp || (otpSent && !verified && countdown > 0)) ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  fontSize: "clamp(12px,3.5vw,13px)"
                }}
              >
                {isSendingOtp ? 'Sending...' : (otpSent && !verified ? `Resend ${countdown > 0 ? `(${countdown}s)` : ''}` : 'Send OTP')}
              </button>
            </div>
            {errors.phone && <div style={errorStyle}>{errors.phone}</div>}
          </div>

          {/* OTP Input + Verify (appears only after OTP sent) */}
          {otpSent && !verified && (
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Enter OTP <span style={{ color: S.danger }}>*</span></label>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  type="text"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit OTP"
                  maxLength="6"
                  style={{ ...inputStyle(errors.otp), flex: 1 }}
                />
                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  style={{
                    padding: "0 20px",
                    borderRadius: 12,
                    border: "none",
                    background: S.green,
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap"
                  }}
                >
                  Verify
                </button>
              </div>
              {errors.otp && <div style={errorStyle}>{errors.otp}</div>}
              <div style={{ fontSize: 11, color: S.muted, marginTop: 6 }}>
                Enter the 6-digit code sent to your phone
              </div>
            </div>
          )}

          {/* Verified success message */}
          {verified && (
            <div style={{ color: S.green, fontSize: 12, marginBottom: 16, textAlign: "center" }}>
              ✓ Phone number verified successfully!
            </div>
          )}

          {/* Submit Button – matches image style (green gradient, lock emoji) */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: "100%",
              background: `linear-gradient(90deg, #2e8e36, #4aad52)`,
              padding: "14px",
              borderRadius: 40,
              color: "#fff",
              fontWeight: 700,
              border: "none",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              fontSize: "clamp(15px,4vw,16px)",
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              opacity: isSubmitting ? 0.7 : 1
            }}
          >
            🔒 {isSubmitting ? "Submitting..." : "Find My Colleges"}
          </button>

          <p style={{ fontSize: 11, color: S.muted, textAlign: 'center', marginTop: 16 }}>
            We respect your privacy. No spam, ever.
          </p>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   COUNSELLING MODAL (unchanged)
───────────────────────────────────────── */
function CounsellingModal({ isOpen, onClose, onSubmit }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [pendingLeadData, setPendingLeadData] = useState(null);
  const [skipOtp, setSkipOtp] = useState(false);
  const [verified, setVerified] = useState(false);

  const validate = () => { const e = {}; if (!name.trim()) e.name='Name is required'; if (!email.trim()) e.email='Email is required'; else if (!/\S+@\S+\.\S+/.test(email)) e.email='Email is invalid'; if (!phone.trim()) e.phone='Phone number is required'; else if (!/^\d{10}$/.test(phone.replace(/\D/g,''))) e.phone='Enter a valid 10-digit phone number'; setErrors(e); return Object.keys(e).length===0; };
  const handleSubmit = (ev) => { ev.preventDefault(); if (!validate()) return; const leadData={name,email,phone}; if (skipOtp) { setIsSending(true); sendLeadEmail({...leadData,otpVerified:false},"Free Counselling Request (OTP Skipped)").then(()=>{setIsSending(false);onSubmit(leadData);setName('');setEmail('');setPhone('');onClose();}); } else if (verified) { setIsSending(true); sendLeadEmail({...leadData,otpVerified:true},"Free Counselling Request (OTP Verified)").then(()=>{setIsSending(false);onSubmit(leadData);setName('');setEmail('');setPhone('');onClose();}); } else { setPendingLeadData(leadData); setShowOtpModal(true); } };
  const handleOTPVerified = async (verifiedStatus) => { if (verifiedStatus && pendingLeadData) { setVerified(true); setIsSending(true); await sendLeadEmail({...pendingLeadData,otpVerified:true},"Free Counselling Request (OTP Verified)"); setIsSending(false); onSubmit(pendingLeadData); setName(''); setEmail(''); setPhone(''); onClose(); } setShowOtpModal(false); setPendingLeadData(null); };

  if (!isOpen) return null;
  return (
    <>
      <OTPVerificationModal isOpen={showOtpModal} onClose={() => setShowOtpModal(false)} onVerify={handleOTPVerified} phoneNumber={phone} actionType="counselling" />
      <div style={modalOverlayStyle}>
        <div style={modalContainerStyle} className="modal-container">
          <div style={modalHeaderStyle} className="modal-header">
            <div>
              <div style={{ fontSize:20, fontWeight:800, color:"#fff" }}>Free Career Counselling</div>
              <div style={{ fontSize:12, color:"#a8e8b0", marginTop:4 }}>Our expert counsellors will guide you</div>
            </div>
            <button onClick={onClose} style={closeButtonStyle}>&times;</button>
          </div>
          <form onSubmit={handleSubmit} style={{ padding:'24px', background:S.white }} className="modal-body">
            {[["text","Full Name","name",name,setName,"e.g. Priya Sharma"],["email","Email Address","email",email,setEmail,"priya@example.com"],["tel","Phone Number","phone",phone,setPhone,"9876543210"]].map(([type,lbl,key,val,setter,ph]) => (
              <div key={key} style={{ marginBottom:20 }}>
                <label style={labelStyle}>{lbl} <span style={{ color:S.danger }}>*</span></label>
                <input type={type} value={val} onChange={e => setter(e.target.value)} placeholder={ph} style={inputStyle(errors[key])} />
                {errors[key] && <div style={errorStyle}>{errors[key]}</div>}
              </div>
            ))}
            <div style={{ marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
              <input type="checkbox" id="skipOtpCounsel" checked={skipOtp} onChange={e => setSkipOtp(e.target.checked)} />
              <label htmlFor="skipOtpCounsel" style={{ fontSize:12, color:S.muted }}>Skip OTP verification (demo mode)</label>
            </div>
            {verified && !skipOtp && <div style={{ color:S.green, fontSize:12, marginBottom:12 }}>✓ Phone number verified</div>}
            <PredictBtn onClick={() => {}} style={{ padding:'12px', fontSize:14 }} disabled={isSending}>
              {isSending ? 'Submitting...' : (skipOtp ? '📞 Request Callback' : (verified ? '📞 Request Callback' : '📱 Send OTP & Request Callback'))}
            </PredictBtn>
            <p style={{ fontSize:11, color:S.muted, textAlign:'center', marginTop:16 }}>
              Mon-Sat: 10 AM – 8 PM | Details sent to <strong style={{ color:S.text }}>dataads@edvisor.co.in</strong>
            </p>
          </form>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────
   RANK COLLEGE CARD & other sections (unchanged from original)
───────────────────────────────────────── */
function RankCollegeCard({ rank, theme, colleges, rangeData, note }) {
  const [expanded, setExpanded] = useState(true);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const handleCollegeClick = (collegeName) => { setSelectedCollege(collegeName); setShowDetailsModal(true); };
  const handleDetailsSubmit = (leadData) => { if (selectedCollege) openGoogleSearch(selectedCollege, leadData); };

  return (
    <div style={{ background:S.white, border:`2px solid ${expanded ? theme.color : S.border}`, borderRadius:16, overflow:"hidden", transition:"all 0.2s", boxShadow:expanded ? `0 4px 20px ${theme.color}30` : "0 2px 8px rgba(0,0,0,0.05)", marginBottom:20 }}>
      <CollegeDetailsModal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} onSubmit={handleDetailsSubmit} collegeName={selectedCollege || ""} />
      <div style={{ padding:"18px 24px", cursor:"pointer", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap", background:`${theme.color}12` }} onClick={() => setExpanded(v => !v)} className="rank-range-header">
        <div style={{ width:56, height:56, background:theme.color, borderRadius:14, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, flexShrink:0, color:"#fff" }} className="rank-icon">{theme.emoji || "🎯"}</div>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:6 }}>
            <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:"clamp(16px,4.5vw,18px)", fontWeight:800, color:S.navy }}>{theme.name}</div>
            <span style={{ background:theme.color, color:"#fff", fontSize:"clamp(9px,3vw,10px)", fontWeight:700, padding:"4px 12px", borderRadius:20 }} className="rank-badge">{theme.badge}</span>
          </div>
          <div style={{ fontSize:"clamp(12px,3.5vw,13px)", color:S.muted }}>Rank Range: {rangeData.minRank.toLocaleString()} – {rangeData.maxRank === Infinity ? "6 Lakh+" : rangeData.maxRank.toLocaleString()}</div>
          <div style={{ fontSize:"clamp(11px,3vw,12px)", color:theme.color, marginTop:4, fontWeight:600 }}>📍 {colleges.length} college{colleges.length !== 1 ? 's' : ''} available</div>
        </div>
        <div style={{ textAlign:"center", flexShrink:0 }} className="expand-btn">
          <div style={{ background:expanded ? theme.color : "#f3f4f6", color:expanded ? "#fff" : theme.color, padding:"8px 18px", borderRadius:30, fontSize:"clamp(12px,3.5vw,13px)", fontWeight:700, transition:"all 0.2s", whiteSpace:"nowrap" }}>
            {expanded ? "▼ Hide Colleges" : "▼ Show All Colleges"}
          </div>
        </div>
      </div>
      {expanded && (
        <div style={{ borderTop:`1px solid ${S.border}`, padding:"clamp(16px,4vw,24px)", background:"#fafafa" }}>
          {note && (
            <div style={{ marginBottom:20, padding:"12px 16px", background:`${theme.color}15`, borderRadius:10, borderLeft:`4px solid ${theme.color}`, fontSize:"clamp(12px,3.5vw,13px)", color:S.text }}>
              <strong>💡 Note:</strong> {note}
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:12 }} className="college-grid">
            {colleges.map((college, idx) => (
              <div key={idx}
                className="college-card"
                style={{ background:S.white, border:`1px solid ${S.border}`, borderRadius:12, padding:"14px 18px", display:"flex", alignItems:"center", gap:14, transition:"all 0.2s", cursor:"pointer", boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor=theme.color; e.currentTarget.style.boxShadow=`0 4px 12px ${theme.color}20`; e.currentTarget.style.transform="translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor=S.border; e.currentTarget.style.boxShadow="0 1px 3px rgba(0,0,0,0.05)"; e.currentTarget.style.transform="translateY(0)"; }}
                onClick={() => handleCollegeClick(college)}>
                <div style={{ width:40, height:40, background:`${theme.color}20`, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>🏥</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:"clamp(13px,4vw,14px)", fontWeight:700, color:S.text }} className="college-name">{college}</div>
                  <div style={{ fontSize:"clamp(10px,3vw,11px)", color:S.muted, marginTop:3 }}>
                    <span style={{ color:theme.color, fontWeight:600 }}>Expected at rank ~{Math.floor((rangeData.minRank + (rangeData.maxRank === Infinity ? rangeData.minRank + 50000 : rangeData.maxRank)) / 2).toLocaleString()}</span>
                  </div>
                </div>
                <div style={{ fontSize:"clamp(18px,5vw,20px)", color:theme.color, cursor:"pointer" }} onClick={e => { e.stopPropagation(); handleCollegeClick(college); }}>→</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop:24, padding:"16px 20px", background:`linear-gradient(135deg,${theme.color}08,${S.white})`, borderRadius:12, border:`1px solid ${theme.color}20` }}>
            <div style={{ fontSize:"clamp(13px,4vw,14px)", color:theme.color, fontWeight:800, marginBottom:8, display:"flex", alignItems:"center", gap:8 }}>
              <span>🎓</span> Personalized Recommendation for Rank {rank.toLocaleString()}
            </div>
            <div style={{ fontSize:"clamp(12px,3.5vw,13px)", color:S.muted, lineHeight:1.6 }}>
              {rank <= 100 ? "🏆 Congratulations! You have an exceptional rank. Focus on AIIMS Delhi and top AIIMS." : rank <= 500 ? "🌟 Excellent rank! Strong chances at premier AIIMS and top GMCs." : rank <= 1000 ? "📊 Very good rank! Top government colleges are within reach." : rank <= 5000 ? "✅ Good rank! Mid-tier GMCs and newer AIIMS are possible." : rank <= 15000 ? "📌 Government colleges still possible, especially newer GMCs." : rank <= 50000 ? "🏥 Private MBBS is your main option. Research deemed universities." : rank <= 150000 ? "🦷 Consider BDS, BAMS, BHMS as alternatives to MBBS." : "📚 Explore alternative healthcare careers like Nursing, Pharmacy, Physiotherapy."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickRankGuide() {
  return (
    <SectionCard>
      <div style={{ background:`linear-gradient(90deg,${S.green},${S.blue})`, color:"#fff", padding:"14px 18px", margin:"-20px -20px 20px -20px", borderRadius:"16px 16px 0 0", fontSize:"clamp(13px,4vw,14px)", fontWeight:700 }}>
        📊 NEET 2026 Quick Rank Guide
      </div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"clamp(11px,3.5vw,12px)", minWidth:250 }}>
          <tbody>
            {[
              ["1-100","AIIMS Delhi, Top GMCs","#ef4444"],
              ["100-500","AIIMS Others, Top GMCs","#f97316"],
              ["500-1000","Premier State GMCs","#d97706"],
              ["1000-5000","Top State GMCs","#16a34a"],
              ["5000-15000","Mid-tier GMCs","#2563eb"],
              ["15000-40000","Lower GMCs","#7c3aed"],
              ["40000-100000","New GMCs","#6d28d9"],
              ["100000-250000","Private Medical Colleges","#374151"],
              ["250000-500000","Lower Budget Private","#1f2937"],
              ["500000+","BDS / AYUSH / Alternative","#047857"]
            ].map(([range, colleges, color]) => (
              <tr key={range} style={{ borderBottom:`1px solid ${S.border}` }}>
                <td style={{ padding:"10px 8px", fontWeight:700, color, width:"80px" }}>{range}</td>
                <td style={{ padding:"10px 8px", color:S.text }}>{colleges}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

function MissionVisionSection() {
  return (
    <SectionCard>
      <SectionTitle icon="🎯">Our Mission & Vision</SectionTitle>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))", gap:20, marginBottom:20 }}>
        <div style={{ background:`${S.green}12`, border:`1.5px solid ${S.green}40`, borderRadius:14, padding:"20px 22px" }}>
          <div style={{ fontSize:32, marginBottom:10 }}>🚀</div>
          <div style={{ fontWeight:800, color:S.navy, fontSize:"clamp(15px,4.5vw,17px)", marginBottom:10 }}>Our Mission</div>
          <div style={{ fontSize:"clamp(12px,3.5vw,13px)", color:S.muted, lineHeight:1.7 }}>
            To provide aspiring medical students with the right guidance and support to pursue MBBS overseas in reputed institutions, ensuring a smooth admission process and a bright future. <strong style={{ color:S.greenDark }}>"Bridging Your Dreams to Reality"</strong>
          </div>
        </div>
        <div style={{ background:`${S.blue}12`, border:`1.5px solid ${S.blue}40`, borderRadius:14, padding:"20px 22px" }}>
          <div style={{ fontSize:32, marginBottom:10 }}>🔭</div>
          <div style={{ fontWeight:800, color:S.navy, fontSize:"clamp(15px,4.5vw,17px)", marginBottom:10 }}>Our Vision</div>
          <div style={{ fontSize:"clamp(12px,3.5vw,13px)", color:S.muted, lineHeight:1.7 }}>
            To be the most reliable and student-centric medical education abroad consultancy, helping students achieve their dreams of becoming qualified doctors by studying MBBS overseas — globally, confidently, successfully.
          </div>
        </div>
      </div>
      <div style={{ background:`linear-gradient(135deg,${S.navy}08,${S.green}08)`, borderRadius:12, padding:"16px 20px", fontSize:"clamp(12px,3.5vw,13px)", color:S.muted, lineHeight:1.7, borderLeft:`4px solid ${S.green}` }}>
        <strong style={{ color:S.navy }}>About Edvisor:</strong> Edvisor Education Overseas is a leading overseas education consultant based in Kolkata, specializing in MBBS admission abroad. Founded with the vision to streamline the study abroad cycle for Indian students, we have grown from a small consultancy into an acknowledged expert in the field.
      </div>
    </SectionCard>
  );
}

function CoursesSection() {
  return (
    <SectionCard>
      <SectionTitle icon="📚">Courses We Help You With</SectionTitle>
      <p style={{ fontSize:"clamp(12px,3.5vw,13px)", color:S.muted, marginBottom:20, lineHeight:1.6 }}>
        From MBBS in India to degrees abroad — Edvisor guides you through admission to top programs across medicine, engineering, management and more.
      </p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))", gap:14 }} className="courses-grid">
        {EDVISOR_COURSES.map((course, idx) => (
          <div key={idx}
            style={{ background:"#f9fafb", border:`1px solid ${S.border}`, borderRadius:12, padding:"16px 14px", textAlign:"center", cursor:"pointer", transition:"all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor=S.green; e.currentTarget.style.background="#f0fdf4"; e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow=`0 6px 16px rgba(74,173,82,0.18)`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor=S.border; e.currentTarget.style.background="#f9fafb"; e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="none"; }}>
            <div style={{ fontSize:"clamp(24px,6vw,30px)", marginBottom:8 }}>{course.icon}</div>
            <div style={{ fontWeight:700, color:S.navy, fontSize:"clamp(13px,4vw,14px)", marginBottom:4 }}>{course.title}</div>
            <div style={{ fontSize:"clamp(10px,3vw,11px)", color:S.muted, lineHeight:1.4 }}>{course.desc}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function MBBSAbroadSection({ onCounsellingClick }) {
  return (
    <SectionCard style={{ background:`linear-gradient(135deg,${S.navy} 0%,#1a3a5c 100%)`, border:"none" }}>
      <SectionTitle icon="✈️" lightMode>
        <span style={{ color:"#fff" }}>Why Study MBBS Abroad with EdVisor?</span>
      </SectionTitle>
      <p style={{ color:"rgba(255,255,255,0.82)", fontSize:"clamp(12px,3.5vw,13px)", marginBottom:20, lineHeight:1.6 }}>
        Over <strong style={{ color:"#7ee888" }}>17,000 Indian students</strong> are pursuing MBBS abroad in 2025. Nearly <strong style={{ color:"#7ee888" }}>70% of graduates</strong> successfully pass the FMGE exam. With EdVisor's expert guidance, your journey is seamless — from university selection to visa and accommodation.
      </p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:14, marginBottom:24 }}>
        {MBBS_ABROAD_BENEFITS.map((benefit, idx) => (
          <div key={idx} style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:12, padding:"16px 18px", display:"flex", gap:14, alignItems:"flex-start" }}>
            <div style={{ fontSize:"clamp(22px,5vw,26px)", flexShrink:0 }}>{benefit.icon}</div>
            <div>
              <div style={{ fontWeight:700, color:"#7ee888", fontSize:"clamp(13px,4vw,14px)", marginBottom:4 }}>{benefit.title}</div>
              <div style={{ fontSize:"clamp(11px,3vw,12px)", color:"rgba(255,255,255,0.68)", lineHeight:1.5 }}>{benefit.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ background:"rgba(255,255,255,0.06)", borderRadius:12, padding:"14px 18px", marginBottom:20, fontSize:"clamp(11px,3vw,12px)", color:"rgba(255,255,255,0.65)", lineHeight:1.6 }}>
        🌍 <strong style={{ color:"#7ee888" }}>Countries:</strong> Russia, Bangladesh, Nepal, Georgia, Kazakhstan, Kyrgyzstan, Uzbekistan, Egypt, China &nbsp;|&nbsp;
        📋 <strong style={{ color:"#7ee888" }}>Recognition:</strong> WHO • NMC • ECFMG approved universities only
      </div>
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", justifyContent:"center" }}>
        <PredictBtn onClick={onCounsellingClick} style={{ maxWidth:280, fontSize:"clamp(12px,3.5vw,13px)", padding:"12px", background:`linear-gradient(90deg,${S.green},${S.blue})` }}>
          ✈️ Explore MBBS Abroad Options →
        </PredictBtn>
      </div>
    </SectionCard>
  );
}

function TestimonialsSection({ onCounsellingClick }) {
  return (
    <SectionCard>
      <SectionTitle icon="💬">What Our Students Say</SectionTitle>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:16, marginBottom:20 }} className="testimonials-grid">
        {EDVISOR_TESTIMONIALS.map((t, idx) => (
          <div key={idx} style={{ background:"linear-gradient(135deg,#f0fdf4,#e8f5e9)", border:`1.5px solid ${S.border}`, borderRadius:14, padding:"18px 20px", display:"flex", flexDirection:"column" }} className="testimonial-card">
            <div style={{ fontSize:28, marginBottom:8 }}>{t.avatar}</div>
            <div style={{ fontSize:"clamp(12px,3.5vw,13px)", color:"#374151", lineHeight:1.7, marginBottom:14, fontStyle:"italic", flex:1 }}>"{t.text}"</div>
            <div style={{ borderTop:`1px solid ${S.border}`, paddingTop:10 }}>
              <div style={{ fontWeight:700, color:S.navy, fontSize:"clamp(13px,4vw,14px)" }}>{t.name}</div>
              <div style={{ fontSize:"clamp(11px,3vw,12px)", color:S.greenDark, fontWeight:600, marginTop:2 }}>{t.course}</div>
              <div style={{ color:S.orange, fontSize:14, marginTop:4 }}>{"★".repeat(t.rating)}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ textAlign:"center" }}>
        <PredictBtn onClick={onCounsellingClick} style={{ maxWidth:320, margin:"0 auto", fontSize:"clamp(12px,3.5vw,13px)", padding:"12px" }}>
          📞 Join 30,000+ Happy Students →
        </PredictBtn>
      </div>
    </SectionCard>
  );
}

function StatsSection() {
  return (
    <SectionCard style={{ textAlign:"center" }}>
      <SectionTitle icon="📊">EdVisor by the Numbers</SectionTitle>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:"clamp(12px,3vw,20px)", marginTop:10 }} className="stats-grid">
        {EDVISOR_STATS.map((stat, idx) => (
          <div key={idx} style={{ padding:"16px", background:"#f0fdf2", borderRadius:12 }} className="stats-card">
            <div style={{ fontSize:"clamp(28px,6vw,32px)", marginBottom:8 }}>{stat.icon}</div>
            <div style={{ fontSize:"clamp(22px,5vw,28px)", fontWeight:800, color:S.greenDark }} className="stats-number">{stat.number}</div>
            <div style={{ fontSize:"clamp(11px,3vw,12px)", color:"#374151" }} className="stats-label">{stat.label}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function CountriesSection() {
  return (
    <SectionCard>
      <SectionTitle icon="🌍">Countries We Serve for MBBS Abroad</SectionTitle>
      <div style={{ display:"flex", flexWrap:"wrap", gap:"clamp(8px,2.5vw,12px)", justifyContent:"center" }}>
        {COUNTRIES_SERVED.map((country, idx) => (
          <span key={idx} style={{ background:"#dcfce7", padding:"6px 14px", borderRadius:30, fontSize:"clamp(11px,3.5vw,13px)", fontWeight:600, color:S.greenDark }} className="country-pill">{country}</span>
        ))}
      </div>
      <p style={{ fontSize:"clamp(12px,3.5vw,13px)", color:S.muted, marginTop:16 }}>
        ✈️ EdVisor has helped 30,000+ students secure admission in top medical universities worldwide.
      </p>
    </SectionCard>
  );
}

function ServicesSection() {
  return (
    <SectionCard>
      <SectionTitle icon="⚡">Our Services</SectionTitle>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:16 }} className="services-grid">
        {EDVISOR_SERVICES.slice(0,6).map((service, idx) => (
          <div key={idx} style={{ padding:"16px", background:"#fafafa", borderRadius:12, border:`1px solid ${S.border}` }} className="service-card">
            <div style={{ fontSize:"clamp(24px,6vw,28px)", marginBottom:8 }}>{service.icon}</div>
            <div style={{ fontSize:"clamp(13px,4vw,14px)", fontWeight:700, color:S.navy, marginBottom:6 }}>{service.title}</div>
            <div style={{ fontSize:"clamp(11px,3vw,12px)", color:S.muted, lineHeight:1.5 }}>{service.description}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

const FAQS = [
  { q:"How accurate is this college predictor?", a:"Our predictor uses historical NEET cutoff data from 2022-2025 and MCC counselling trends to provide realistic college expectations." },
  { q:"What is AIQ vs State Quota?", a:"AIQ is 15% of government medical seats open to all states. State quota (85%) is for domicile holders." },
  { q:"How does category affect my rank?", a:"SC/ST/OBC categories have relaxed cutoffs compared to General." },
  { q:"Why do I need OTP verification?", a:"OTP verification ensures we have your correct contact details. You can skip OTP by checking the 'Skip OTP' checkbox." },
  { q:"What are my options if I don't get MBBS?", a:"Alternative careers include BDS, BAMS, BHMS, Nursing, Pharmacy, Physiotherapy, BSc courses, and Paramedical programs." },
  { q:"Does EdVisor help with MBBS abroad?", a:"Yes! EdVisor has partnered with 100+ universities worldwide for MBBS in Russia, Bangladesh, Nepal, Georgia, etc." },
  { q:"Where do my details go?", a:"All your information is sent to dataads@edvisor.co.in for counselling follow-up." },
  { q:"Does EdVisor charge for counselling?", a:"Initial career counselling sessions are free. EdVisor offers lowest package for admission and handles your complete admission process." },
  { q:"What courses does EdVisor help with besides MBBS?", a:"EdVisor guides students for MBBS, BDS, Nursing, MBA, BTech, BAMS, AYUSH, Pharmacy, and postgraduate (MD/MS/MDS) courses both in India and abroad." },
  { q:"Which countries does EdVisor cover for MBBS abroad?", a:"Russia, Bangladesh, Nepal, Georgia, Kazakhstan, Kyrgyzstan, Uzbekistan, Egypt, and China — all with WHO/NMC recognized universities." }
];

/* ─────────────────────────────────────────
   MAIN COMPONENT (unchanged)
───────────────────────────────────────── */
export default function EdvisorNEETCollegePredictor() {
  const [rank, setRank] = useState("");
  const [category, setCategory] = useState("General (UR)");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [isPredModalOpen, setIsPredModalOpen] = useState(false);
  const [isCounsellingOpen, setIsCounsellingOpen] = useState(false);
  const [pendingRank, setPendingRank] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const resultRef = useRef(null);

  const heroBgImageUrl = "https://images.pexels.com/photos/2570062/pexels-photo-2570062.jpeg?auto=compress&cs=tinysrgb&w=1600";

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes edv-spin { to { transform: rotate(360deg); } }
      @keyframes edv-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      @keyframes edv-fadeIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Poppins', sans-serif; background: #f4faf5; overflow-x: hidden; }
      
      /* Desktop: side by side layout */
      .hero-grid {
        display: grid;
        grid-template-columns: 1fr 420px;
        gap: clamp(20px,5vw,40px);
        align-items: center;
      }
      
      /* Hide mobile-only text by default */
      .mobile-only-text { display: none; }
      
      /* Show desktop text by default */
      .desktop-only-text { display: inline; }
      
      /* Tablet and Mobile: column layout (text on top, form below) */
      @media (max-width: 1100px) {
        .hero-grid { 
          grid-template-columns: 1fr !important; 
          gap: 2rem !important; 
          text-align: center;
        }
        .hero-grid > div:first-child { 
          order: 1;
        }
        .hero-grid > div:last-child { 
          order: 2;
          max-width: 500px; 
          margin: 0 auto; 
          width: 100%; 
        }
        .hero-text { 
          text-align: center; 
        }
        .hero-text p { 
          margin-left: auto; 
          margin-right: auto; 
        }
        .main-grid { 
          grid-template-columns: 1fr !important; 
          gap: 2rem !important;
        }
        .footer-grid { 
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important; 
        }
        .stats-grid { 
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)) !important; 
        }
        /* Hide desktop navigation links on mobile/tablet */
        .desktop-nav {
          display: none !important;
        }
        /* Show mobile-specific topbar text */
        .mobile-only-text {
          display: inline !important;
        }
        .desktop-only-text {
          display: none !important;
        }
      }
      
      @media (max-width: 768px) {
        .hero-grid { padding: 40px 16px !important; }
        .hero-text h1 { font-size: 1.8rem !important; }
        .rank-range-header { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
        .expand-btn { align-self: center !important; margin-top: 8px !important; }
        .nav-links { gap: 8px !important; }
        .nav-links a { font-size: 12px !important; padding: 4px 8px !important; }
        .topbar-content { flex-direction: column !important; text-align: center !important; gap: 8px !important; }
        .marquee-text { font-size: 11px !important; }
        .section-padding { padding: 16px !important; }
        .modal-container { width: 95% !important; margin: 16px !important; }
        .modal-header { padding: 16px !important; }
        .modal-body { padding: 20px !important; }
        .college-card { padding: 12px 16px !important; }
        .rank-number { font-size: 2.5rem !important; }
        .result-header { flex-direction: column !important; text-align: center !important; }
        .services-grid { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)) !important; }
        .testimonials-grid { grid-template-columns: 1fr !important; }
      }
      
      @media (max-width: 550px) {
        .hero-text h1 { font-size: 1.5rem !important; }
        .college-grid { grid-template-columns: 1fr !important; }
        .courses-grid { grid-template-columns: repeat(2, 1fr) !important; }
        .services-grid { grid-template-columns: 1fr !important; }
        .testimonials-grid { grid-template-columns: 1fr !important; }
        .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
        .rank-icon { width: 40px !important; height: 40px !important; font-size: 20px !important; }
        .college-name { font-size: 12px !important; }
        .predict-btn { padding: 10px 16px !important; font-size: 13px !important; }
        .country-pill { padding: 4px 10px !important; font-size: 10px !important; }
        .footer-address { flex-direction: column !important; gap: 8px !important; text-align: center !important; }
      }
      
      /* Touch-friendly for mobile devices */
      @media (hover: none) and (pointer: coarse) {
        button, [onClick], .clickable, .college-card, .rank-range-header {
          min-height: 44px;
          touch-action: manipulation;
        }
        input, select, textarea {
          font-size: 16px !important;
        }
      }
      
      details summary { list-style: none; cursor: pointer; }
      details summary::-webkit-details-marker { display: none; }
    `;
    document.head.appendChild(style);
    emailjs.init(EMAILJS_PUBLIC_KEY);
    return () => document.head.removeChild(style);
  }, []);

  const runPrediction = (leadInfo) => {
    const r = parseInt(rank);
    if (!r || r <= 0 || r > 600000) { alert("Please enter a valid NEET rank between 1 and 6,00,000."); return; }
    setLoading(true);
    setTimeout(() => {
      const { rangeData, colleges, note } = getCollegesForRank(r);
      const theme = getThemeForRank(r);
      setResults({ rank:r, category, colleges, rangeData, theme, note });
      setLoading(false);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior:"smooth", block:"start" }), 100);
    }, 500);
  };

  const handleHeroPredictClick = () => {
    const r = parseInt(rank);
    if (!r || r <= 0 || r > 600000) { alert("Please enter a valid NEET rank between 1 and 6,00,000."); return; }
    setPendingRank(r); setIsPredModalOpen(true);
  };

  const handlePredLeadSubmit = (leadInfo) => { if (pendingRank) { setRank(pendingRank.toString()); runPrediction(leadInfo); setPendingRank(null); } };
  const openWhatsApp = (type) => { const msg = type === 'help' ? "Hello, I need help with EdVisor NEET College Predictor." : "Hello, I'd like to know more about EdVisor's services."; window.open(`https://wa.me/918584826222?text=${encodeURIComponent(msg)}`, '_blank'); };
  const openWhatsAppLead = (lead) => { window.open(`https://wa.me/918584826222?text=${encodeURIComponent(`Hello, I'm ${lead.name}. Email: ${lead.email}, Phone: ${lead.phone}. I need free counselling for NEET college selection.`)}`, '_blank'); };

  const hero = (
    <div className="hero-grid" style={{
      backgroundImage: `linear-gradient(rgba(10, 26, 46, 0.65), rgba(10, 26, 46, 0.65)), url(${heroBgImageUrl})`,
      backgroundSize: "cover", 
      backgroundPosition: "center 30%", 
      backgroundRepeat: "no-repeat",
      padding: "clamp(60px, 12vw, 100px) 20px clamp(40px, 8vw, 70px)",
      position: "relative",
      minHeight: "55vh"
    }}>
      <div style={{ maxWidth: 1200, margin: "auto", display: "contents" }}>
        <div className="hero-text">
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(74,173,82,0.25)", border: "1px solid rgba(74,173,82,0.6)", color: "#a8f0b0", fontSize: "clamp(10px, 3vw, 12px)", fontWeight: 700, padding: "4px 12px", borderRadius: 20, marginBottom: 16, textTransform: "uppercase", letterSpacing: 0.5 }}>
            ● Rank-Wise Predictor 2026
          </div>
          <h1 style={{ fontFamily: "'Nunito', sans-serif", fontSize: "clamp(2rem, 7vw, 3rem)", fontWeight: 900, color: "#fff", lineHeight: 1.2, marginBottom: 14, textShadow: "0 2px 12px rgba(0,0,0,0.3)" }} className="hero-title">
            EdVisor <span style={{ color: "#8ae89a" }}>NEET Rank-Wise</span> College Predictor 2026
          </h1>
          <p style={{ color: "rgba(255,255,255,0.92)", fontSize: "clamp(14px, 4vw, 16px)", lineHeight: 1.7, maxWidth: 540, marginBottom: 24, fontWeight: 500, textShadow: "0 1px 3px rgba(0,0,0,0.2)" }} className="hero-subtext">
            Enter your NEET rank and instantly discover exactly which colleges you can get — from AIIMS Delhi to backup options. Complete rank-wise database with 45+ rank ranges and 500+ colleges.
          </p>
        </div>
        <div style={{ background: S.white, borderRadius: "24px 24px 24px 24px", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.45)", overflow: "hidden" }}>
          <div style={{ background: `linear-gradient(135deg, ${S.green} 0%, ${S.blue} 100%)`, padding: "18px 24px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ width: 42, height: 42, background: "rgba(255,255,255,0.2)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🏥</div>
            <div>
              <div style={{ color: "#fff", fontSize: "clamp(15px, 4vw, 16px)", fontWeight: 800 }}>Find Your Medical College</div>
              <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "clamp(11px, 3vw, 12px)" }}>Enter your NEET rank below</div>
            </div>
          </div>
          <div style={{ padding: "24px 26px 28px", background: S.white }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: "clamp(13px, 3.5vw, 14px)", fontWeight: 700, color: S.text, display: "block", marginBottom: 8 }}>NEET Rank (AIR) <span style={{ color: S.danger }}>*</span></label>
              <input type="number" min="1" max="600000" placeholder="e.g. 5000" value={rank} onChange={e => setRank(e.target.value)} className="rank-input"
                style={{ width: "100%", border: `2px solid ${S.border}`, borderRadius: 14, padding: "12px 16px", fontSize: "clamp(18px, 5vw, 22px)", fontFamily: "'Nunito', sans-serif", fontWeight: 800, outline: "none", background: "#f0fdf4", color: S.text, transition: "all 0.2s" }} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: "clamp(13px, 3.5vw, 14px)", fontWeight: 700, color: S.text, display: "block", marginBottom: 8 }}>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: "100%", border: `2px solid ${S.border}`, borderRadius: 14, padding: "12px 16px", fontSize: "clamp(13px, 4vw, 14px)", fontFamily: "inherit", outline: "none", background: "#f0fdf4", color: S.text, fontWeight: 500 }}>
                {["General (UR)", "OBC-NCL", "SC", "ST", "EWS", "General-PwD", "OBC-PwD"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div style={{ marginTop: 8 }}>
              <PredictBtn onClick={handleHeroPredictClick}>🎯 Find Colleges by Rank</PredictBtn>
            </div>
            <p style={{ fontSize: "clamp(11px, 3vw, 12px)", color: S.muted, textAlign: "center", marginTop: 14 }}>Free • Instant • Complete rank-wise database • OTP optional (skip available)</p>
          </div>
        </div>
      </div>
    </div>
  );

  const resultPanel = results && (
    <div ref={resultRef} style={{ animation: "edv-fadeIn 0.4s ease" }}>
      <div style={{ background: `linear-gradient(135deg, ${results.theme.color}15 0%, ${S.white} 100%)`, borderRadius: 16, padding: "20px 24px", marginBottom: 20, display: "flex", gap: "clamp(12px, 3vw, 20px)", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", border: `1px solid ${results.theme.color}30` }} className="result-header">
        <div>
          <div style={{ color: S.muted, fontSize: "clamp(12px, 3.5vw, 13px)" }}>Your NEET Rank</div>
          <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: "clamp(36px, 10vw, 52px)", fontWeight: 900, color: results.theme.color, lineHeight: 1 }} className="rank-number">{results.rank.toLocaleString("en-IN")}</div>
          <div style={{ fontSize: "clamp(11px, 3vw, 13px)", color: S.muted, marginTop: 6 }}>Category: <strong style={{ color: S.text }}>{results.category}</strong></div>
        </div>
        <div style={{ background: S.white, borderRadius: 14, padding: "14px 24px", textAlign: "center", minWidth: 160, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize: "clamp(11px, 3vw, 13px)", color: S.muted }}>Your Rank Range</div>
          <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: "clamp(18px, 5vw, 20px)", fontWeight: 800, color: results.theme.color }}>{results.rangeData.rankRange}</div>
          <div style={{ fontSize: "clamp(11px, 3vw, 12px)", color: results.theme.color, marginTop: 6, fontWeight: 600 }}>🎯 {results.colleges.length} colleges available</div>
        </div>
        <PredictBtn onClick={() => setIsCounsellingOpen(true)} style={{ padding: "clamp(10px, 3vw, 12px) 24px", fontSize: "clamp(12px, 3.5vw, 13px)", width: "auto" }}>📞 Get Expert Advice</PredictBtn>
      </div>
      <RankCollegeCard rank={results.rank} theme={results.theme} colleges={results.colleges} rangeData={results.rangeData} note={results.note} />
      <div style={{ marginTop: "20px" }}><QuickRankGuide /></div>
    </div>
  );

  const sidebar = (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ background: S.white, borderRadius: 16, border: `1.5px solid ${S.border}`, overflow: "hidden" }}>
        <div style={{ background: `linear-gradient(90deg, ${S.navy}, #1a3a6c)`, color: "#fff", padding: "14px 18px", fontSize: "clamp(13px, 4vw, 14px)", fontWeight: 700 }}>
          🛠️ How It Works
        </div>
        <ul style={{ padding: "18px 20px", listStyle: "none" }}>
          {["1️⃣ Enter your NEET rank", "2️⃣ Select your category", "3️⃣ Click 'Find Colleges by Rank'", "4️⃣ Fill your details (OTP optional - can skip)", "5️⃣ View complete college list for your range", "6️⃣ Expand to see all colleges", "7️⃣ Click any college to get details (OTP optional)", "8️⃣ Book free counselling for personalized advice"].map((item, i) => (
            <li key={i} style={{ marginBottom: 12, fontSize: "clamp(12px, 3.5vw, 13px)", color: S.text, display: "flex", alignItems: "flex-start", gap: 8 }}>
              <span style={{ fontSize: "clamp(14px, 4vw, 16px)" }}>{item.substring(0, 2)}</span>
              <span style={{ color: S.muted }}>{item.substring(3)}</span>
            </li>
          ))}
        </ul>
      </div>
      <div style={{ background: "linear-gradient(135deg, #fff8ed, #fdecd0)", border: `1.5px solid #f5d89a`, borderRadius: 16, padding: "clamp(16px, 5vw, 24px)", textAlign: "center" }}>
        <div style={{ fontSize: "clamp(32px, 8vw, 40px)", marginBottom: 12 }}>🎯</div>
        <div style={{ fontSize: "clamp(14px, 4vw, 16px)", fontWeight: 800, color: S.navy, marginBottom: 8 }}>Get Free Counselling</div>
        <div style={{ fontSize: "clamp(12px, 3.5vw, 13px)", color: "#78350f", marginBottom: 16, lineHeight: 1.5 }}>
          Talk to our NEET experts for personalised college selection and admission guidance based on your rank
        </div>
        <PredictBtn onClick={() => setIsCounsellingOpen(true)} style={{ fontSize: "clamp(12px, 3.5vw, 14px)", padding: "clamp(10px, 3vw, 12px)" }}>
          📞 Book Free Session →
        </PredictBtn>
      </div>
    </div>
  );

  const footer = (
    <footer style={{ background: "#0a1a0e", color: "#6a9a72", padding: "40px 20px 20px", marginTop: 40 }}>
      <div style={{ maxWidth: 1200, margin: "auto" }}>
        <div className="footer-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "clamp(20px, 5vw, 30px)", marginBottom: 30 }}>
          <div>
            <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: "clamp(20px, 5vw, 22px)", fontWeight: 900, color: "#fff", marginBottom: 12 }}>
              <span style={{ color: S.green }}>Ed</span>Visor
            </div>
            <p style={{ fontSize: "clamp(12px, 3.5vw, 13px)", lineHeight: 1.6, color: "#7aaa82" }}>India's most trusted platform for medical college admissions guidance.</p>
            <div style={{ marginTop: 16, fontSize: "clamp(11px, 3vw, 12px)", color: "#8acc92" }}>
              <div>📞 +91 85848 26222</div>
              <div>✉️ dataads@edvisor.co.in</div>
            </div>
          </div>
          {[
            { title: "Predictors", links: ["NEET College Predictor", "NEET PG Predictor", "Rank Predictor", "AIIMS Predictor"] },
            { title: "Resources", links: ["Rank vs College Guide", "Category-wise Cutoffs", "State Quota Info", "Counselling Guide"] },
            { title: "Quick Links", links: ["About Us", "Contact Us", "Study Abroad", "MBBS Abroad"] }
          ].map(col => (
            <div key={col.title}>
              <h4 style={{ color: "#a0e8b0", fontSize: "clamp(13px, 4vw, 14px)", marginBottom: 16, fontWeight: 700 }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#6a9a72", fontSize: "clamp(11px, 3.5vw, 13px)", marginBottom: 10, textDecoration: "none" }}
                  onMouseEnter={e => { e.currentTarget.style.color = "#a0e8b0"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "#6a9a72"; }}>{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid #1a3a1e", paddingTop: 20, marginBottom: 20 }}>
          <div className="footer-address" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 20, fontSize: "clamp(11px, 3vw, 12px)", color: "#8acc92" }}>
            <div><strong style={{ color: "#a0e8b0" }}>📍 Kolkata:</strong> 2C Dimple Court, 26 Shakespeare Sarani Rd, Elgin, Kolkata - 700017</div>
            <div><strong style={{ color: "#a0e8b0" }}>📍 Bangalore:</strong> 708, 7th Floor, North Rear Wing, Manipal Centre, M.G Road, Bangalore - 560001</div>
          </div>
        </div>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <button onClick={() => setShowAbout(!showAbout)}
            style={{ background: "transparent", border: `1.5px solid ${S.green}`, color: S.green, padding: "8px 28px", borderRadius: 30, fontSize: "clamp(12px, 3.5vw, 13px)", fontWeight: 600, cursor: "pointer" }}>
            {showAbout ? "✖ Close" : "ℹ️ About EdVisor"}
          </button>
        </div>
        {showAbout && (
          <div style={{ background: S.white, borderRadius: 16, padding: "clamp(20px, 5vw, 28px) clamp(16px, 5vw, 32px)", marginTop: 20, textAlign: "center" }}>
            <h3 style={{ color: S.navy, marginBottom: 12, fontSize: "clamp(18px, 5vw, 22px)" }}>About EdVisor</h3>
            <p style={{ fontSize: "clamp(12px, 3.5vw, 14px)", color: S.muted, lineHeight: 1.6 }}>EdVisor is India's leading education guidance platform, helping students find the right colleges through AI-powered predictors and expert counselling. Our NEET rank-wise database is updated annually based on MCC counselling data from the last 4 years. With 18+ years of experience and 50,000+ students counselled, we are committed to bridging your dreams to reality.</p>
            <p style={{ fontSize: "clamp(11px, 3vw, 13px)", color: S.green, marginTop: 16 }}>🌍 www.edvisor.co.in | Mon-Sat: 10 AM – 8 PM | All leads sent to dataads@edvisor.co.in</p>
          </div>
        )}
        <div style={{ borderTop: "1px solid #1a3a1e", paddingTop: 20, textAlign: "center", fontSize: "clamp(10px, 3vw, 12px)", color: "#4a7a52" }}>
          © 2026 EdVisor India Pvt. Ltd. | Predictions are estimates based on historical NEET data. Always check official MCC counselling.
        </div>
      </div>
    </footer>
  );

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif", background: S.bg, color: S.text, minHeight: "100vh", animation: "edv-fadeIn 0.5s ease", overflowX: "hidden" }}>
      <Spinner show={loading} />
      <PredictionModal isOpen={isPredModalOpen} onClose={() => { setIsPredModalOpen(false); setPendingRank(null); }} onSubmit={handlePredLeadSubmit} rankValue={pendingRank} />
      <CounsellingModal isOpen={isCounsellingOpen} onClose={() => setIsCounsellingOpen(false)} onSubmit={openWhatsAppLead} />
      <Topbar onCounsellingClick={() => setIsCounsellingOpen(true)} onHelpClick={() => openWhatsApp('help')} onAboutClick={() => openWhatsApp('about')} />
      <Navbar />
      <Marquee />
      {hero}
      <div className="main-grid" style={{ maxWidth: 1200, margin: "0 auto", padding: "clamp(20px, 5vw, 30px) 20px 60px", display: "grid", gridTemplateColumns: "1fr 340px", gap: "clamp(20px, 4vw, 28px)" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <StatsSection />
          <MissionVisionSection />
          {resultPanel}
          <CountriesSection />
          <MBBSAbroadSection onCounsellingClick={() => setIsCounsellingOpen(true)} />
          <ServicesSection />
          <CoursesSection />
          <TestimonialsSection onCounsellingClick={() => setIsCounsellingOpen(true)} />
          <SectionCard>
            <SectionTitle icon="❓">Frequently Asked Questions</SectionTitle>
            {FAQS.map((f, i) => (
              <div key={i} style={{ borderBottom: i === FAQS.length - 1 ? "none" : `1px solid ${S.border}`, padding: "16px 0" }}>
                <details>
                  <summary style={{ fontSize: "clamp(13px, 4vw, 14px)", fontWeight: 600, color: S.text, cursor: "pointer", padding: "4px 0" }} className="faq-summary">{f.q}</summary>
                  <p style={{ fontSize: "clamp(12px, 3.5vw, 13px)", color: S.muted, marginTop: 12, lineHeight: 1.6, paddingLeft: 8 }}>{f.a}</p>
                </details>
              </div>
            ))}
          </SectionCard>
        </div>
        {sidebar}
      </div>
      {footer}
    </div>
  );
}